import React, { useCallback, useEffect, useState } from "react";
import { LayoutTemplate } from "lucide-react";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import { listTemplates } from "./imageCreationService";

const selectCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none disabled:bg-gray-50";

/**
 * Optional "Base template" dropdown for Image Creation. Lists this store's APPROVED templates only (drafts must be
 * approved in Base Template Creation first). `onChange(template | null)` gets the whole template, so the parent knows
 * whether it brings its own image.
 */
export default function TemplatePicker({ value, onChange, disabled }) {
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listTemplates()
      .then((list) => {
        const approved = list.filter((t) => t.status === "approved");
        setTemplates(approved);
        setDrafts(list.length - approved.length);
        // The chosen template was deleted or edited back to draft elsewhere: drop the selection.
        if (value && !approved.some((t) => t.id === value)) onChange(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, load);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, load);
  }, [load]);

  const hint = error
    ? error
    : templates.length === 0 && !loading
      ? drafts > 0
        ? `You have ${drafts} draft template${drafts === 1 ? "" : "s"}. Approve one in Base Template Creation to use it here.`
        : "No saved templates yet. Create one in Base Template Creation (Library → Add to Components)."
      : "Applies your saved branding and layout to the image.";

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="ic-base-template" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
        <LayoutTemplate size={14} className="text-indigo-600" />
        Base template <span className="font-normal text-gray-400">(optional)</span>
      </label>
      <select
        id="ic-base-template"
        className={selectCls}
        value={value || ""}
        disabled={disabled || loading}
        onChange={(e) => onChange(templates.find((t) => t.id === e.target.value) || null)}
        onFocus={load}
      >
        <option value="">{loading ? "Loading templates…" : "No template"}</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <span className={`text-xs ${error ? "text-red-600" : "text-gray-500"}`}>{hint}</span>
    </div>
  );
}
